import React, { useEffect, useState, useRef, useCallback } from "react";
import { Card, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  getDigitalRamNameCount,
  submitDigitalRamNameApi,
} from "../RamNamBookCRUD";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const TOTAL = 108;
const getOSInfo = () => {
  const ua = navigator.userAgent;

  if (ua.includes("Android")) return "Android";
  if (ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
  if (ua.includes("Windows")) return "Windows";
  if (ua.includes("Mac")) return "MacOS";

  return "Unknown";
};

const getBrowserName = () => {
  const ua = navigator.userAgent;

  if (ua.includes("Edg")) return "Edge";
  if (ua.includes("OPR")) return "Opera";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  if (ua.includes("Firefox")) return "Firefox";

  return "Unknown";
};

const getOSVersion = () => {
  return navigator.userAgent;
};

const getDeviceID = () => {
  let id = localStorage.getItem("device_id");

  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("device_id", id);
  }

  return id;
};
// ─── Mala Canvas ─────────────────────────────────────────────────────────────
const MalaCanvas = ({ count, size = 300, ramText = "રામ" }) => {
  const canvasRef = useRef(null);
  const prevRef = useRef(count);

  const flowerImgRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.src = "/assets/newImage.jpeg"; // ← your image path
    img.onload = () => {
      flowerImgRef.current = img;
      // force redraw once image is ready
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        const active = count % TOTAL;
        redraw(ctx, active);
      }
    };
  }, []); // runs once on mount

  const CX = size / 2;
  const CY = size / 2;
  const R = size / 2 - 18;

  const drawFlower = useCallback(
    (ctx, x, y, r, angle) => {
      const PETALS = 8;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);

      for (let p = 0; p < PETALS; p++) {
        ctx.save();
        ctx.rotate((p / PETALS) * 2 * Math.PI);
        ctx.beginPath();
        ctx.ellipse(0, -r * 0.72, r * 0.28, r * 0.48, 0, 0, 2 * Math.PI);
        const pg = ctx.createRadialGradient(
          0,
          -r * 0.55,
          0,
          0,
          -r * 0.55,
          r * 0.48,
        );
        pg.addColorStop(0, "#ff6600");
        pg.addColorStop(1, "#cc1a00");
        ctx.fillStyle = pg;
        ctx.fill();
        ctx.restore();
      }

      const cr = r * 0.52;
      const cg = ctx.createRadialGradient(-cr * 0.2, -cr * 0.3, 0, 0, 0, cr);
      cg.addColorStop(0, "#ff8833");
      cg.addColorStop(1, "#cc7a00");
      ctx.beginPath();
      ctx.arc(0, 0, cr, 0, 2 * Math.PI);
      ctx.fillStyle = cg;
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.6)";
      ctx.lineWidth = 0.8;
      ctx.stroke();

      ctx.rotate(-angle);
      const fontSize = Math.max(Math.floor(cr * 1.4), 6);
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(ramText, 0, 0);

      ctx.restore();
    },
    [ramText],
  );

  const redraw = useCallback(
    (ctx, n) => {
      ctx.clearRect(0, 0, size, size);

      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, 2 * Math.PI);
      ctx.strokeStyle = "#7a4a08";
      ctx.lineWidth = size < 270 ? 10 : 13;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, 2 * Math.PI);
      ctx.strokeStyle = "#c8882a";
      ctx.lineWidth = size < 270 ? 6 : 8;
      ctx.stroke();

      const flowerR = size < 270 ? 13 : 16;

      for (let i = 0; i < n; i++) {
        const angle = (i / TOTAL) * 2 * Math.PI - Math.PI / 2;
        const x = CX + R * Math.cos(angle);
        const y = CY + R * Math.sin(angle);
        drawFlower(ctx, x, y, flowerR, angle + Math.PI / 2);
      }

      const omR = size < 270 ? 13 : 16;
      const omX = CX;
      const omY = CY - R;
      ctx.save();
      ctx.beginPath();
      ctx.arc(omX, omY, omR, 0, 2 * Math.PI);
      const og = ctx.createRadialGradient(omX - 2, omY - 3, 0, omX, omY, omR);
      og.addColorStop(0, "#f8b31d");
      og.addColorStop(1, "#fce945");
      ctx.fillStyle = og;
      ctx.fill();
      ctx.strokeStyle = "#f32e14";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.font = `bold ${omR - 2}px serif`;
      ctx.fillStyle = "#8B0000";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("ॐ", omX, omY + 1);
      ctx.restore();
    },
    [CX, CY, R, size, drawFlower],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const active = count % TOTAL;
    const prev = prevRef.current % TOTAL;
    prevRef.current = count;
    if (count > 0 && active === 0 && prev !== 0) {
      redraw(ctx, 0);
      return;
    }
    redraw(ctx, active);
  }, [count, redraw, size, ramText]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ display: "block" }}
    />
  );
};

// ─── Mala Section ─────────────────────────────────────────────────────────────
const MalaSection = ({ count, malas, size, onTap, getText, loading }) => {
  const [visible, setVisible] = useState(true);
  const cooldownRef = useRef(false);
  const active = count % TOTAL;
  const btnSize = size < 270 ? 108 : 120;
  const ramText = getText("RAM", "राम", "રામ");

  const handleTap = () => {
    if (loading || cooldownRef.current) return;
    cooldownRef.current = true;
    setVisible(false);
    setTimeout(() => {
      setVisible(true);
      cooldownRef.current = false;
    }, 400);
    onTap();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
      }}
    >
      <style>{`
        @keyframes btnAppear {
          0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.7);  }
          60%  { opacity: 1; transform: translate(-50%,-50%) scale(1.08); }
          100% { opacity: 1; transform: translate(-50%,-50%) scale(1);    }
        }
        @keyframes btnDisappear {
          0%   { opacity: 1; transform: translate(-50%,-50%) scale(1);   }
          100% { opacity: 0; transform: translate(-50%,-50%) scale(0.7); }
        }
        .btn-hide { animation: btnDisappear 0.15s ease forwards; }
        .btn-show { animation: btnAppear   0.35s ease forwards; }
      `}</style>

      <div style={{ position: "relative", width: size, height: size }}>
        <MalaCanvas count={count} size={size} ramText={ramText} />

        <button
          onClick={handleTap}
          className={visible ? "btn-show" : "btn-hide"}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%) scale(1)",
            width: btnSize,
            height: btnSize,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 38% 32%, #ffe8ec, #f87822 55%, #f49033f3)",
            border: "none",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            boxShadow:
              "0 4px 22px rgba(220,80,80,0.35), inset 0 3px 10px rgba(255,255,255,0.55)",
            zIndex: 10,
          }}
        >
          <span
            style={{
              fontSize: btnSize < 145 ? 26 : 30,
              fontWeight: 700,
              color: "#441414",
              fontFamily: "serif",
              lineHeight: 1,
              textShadow: "0 1px 4px rgba(255,255,255,0.6)",
            }}
          >
            {getText("RAM", "राम", "રામ")}
          </span>
          <span
            style={{
              fontSize: 9,
              color: "#75200b",
              marginTop: 4,
              opacity: 0.85,
            }}
          >
            {getText(
              "Tap to Jaap",
              "जपने के लिए टैप करें",
              "જાપ કરવા માટે ટેપ કરો",
            )}
          </span>
        </button>
      </div>

      {/* Mini stats */}
      <div style={{ display: "flex", gap: 20 }}>
        <div style={{ textAlign: "center", minWidth: 56 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#cc2200" }}>
            {active}
          </div>
          <div style={{ fontSize: 11, color: "#888" }}>
            {getText("Count", "गणना", "ગણતરી")}
          </div>
        </div>
        <div style={{ textAlign: "center", minWidth: 56 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#cc2200" }}>
            {malas}
          </div>
          <div style={{ fontSize: 11, color: "#888" }}>
            {getText("Malas", "माला", "માળા")}
          </div>
        </div>
        <div style={{ textAlign: "center", minWidth: 56 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#076d07" }}>
            {count}
          </div>
          <div style={{ fontSize: 11, color: "#888" }}>
            {getText("Total", "कुल", "કુલ")}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DigitalRamTapCard = ({ tabRamNam }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const [todayCount, setTodayCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [prevRamNam, setPrevRamNam] = useState(tabRamNam || 0);
  const [memberId, setMemberId] = useState(null);
  const [malas, setMalas] = useState(0);

  const getText = (en, hi, gu) => {
    if (i18n.language === "hi") return hi;
    if (i18n.language === "gu") return gu;
    return en;
  };

  // ✅ SCROLL FIX: Remove overflow:hidden from body on mount, restore on unmount
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "auto";
    document.body.style.height = "auto";
    document.documentElement.style.overflow = "auto";

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.height = originalHeight;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    const storedCustomer = localStorage.getItem("customer");
    if (!storedCustomer) {
      toast.error("Member not found. Please login again.");
      return;
    }
    const customerObj = JSON.parse(storedCustomer);
    if (!customerObj?.ramNamBookMemberID) {
      toast.error("Invalid member data");
      return;
    }
    setMemberId(customerObj.ramNamBookMemberID);
  }, []);

  useEffect(() => {
    if (memberId) loadTotalCount(memberId);
  }, [memberId]);

  const loadTotalCount = async (id) => {
    try {
      setLoading(true);
      const res = await getDigitalRamNameCount(id);
      if (res.data?.isSuccess) setPrevRamNam(res.data.data);
      else toast.error(res.data?.message || "Failed to load total");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRamTap = () => {
    setTodayCount((prev) => {
      const next = prev + 1;
      if (next % TOTAL === 0) setMalas((m) => m + 1);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (todayCount === 0) return;
    try {
      setLoading(true);
      const osInfo = getOSInfo();
      const deviceModelNo = getBrowserName();
      const osVersion = getOSVersion();
      const deviceID = getDeviceID();

      const res = await submitDigitalRamNameApi(
        memberId,
        todayCount,
        2,
        osInfo,
        deviceModelNo,
        osVersion,
        deviceID,
      );
      if (res.data?.isSuccess) {
        toast.success(res.data.message);
        setTodayCount(0);
        setMalas(0);
        loadTotalCount(memberId);
      } else {
        toast.error(res.data?.message || "Something went wrong");
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const sharedMalaProps = {
    count: todayCount,
    malas,
    onTap: handleRamTap,
    getText,
    loading,
  };

  return (
    // ✅ SCROLL FIX: Outer wrapper allows full scroll
    <div
      style={{
        minHeight: "100vh",
        overflowY: "auto",
        paddingBottom: "2rem",
      }}
    >
      {/* ═══════════ DESKTOP ═══════════ */}
      <div className="d-none d-md-flex justify-content-center px-4">
        <Card
          className="border-0 rounded-4 shadow-lg w-100"
          style={{
            maxWidth: "900px",
            background: "linear-gradient(135deg,#fff7f0,#fff)",
          }}
        >
          <Card.Body className="p-1 p-md-5">
            <div className="row align-items-start">
              {/* LEFT — Ram image */}
              <div className="col-12 col-md-5 text-center mb-5 mb-md-0 d-flex flex-column align-items-center justify-content-start pt-md-1">
                <div
                  className="d-flex align-items-start justify-content-center"
                  style={{
                    width: 280,
                    height: 280,
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(255,138,0,0.35), transparent 70%)",
                  }}
                >
                  <img
                    src="/assets/logo/RAMBhagvan.png"
                    alt="Shri Ram"
                    className="rounded-circle shadow"
                    style={{
                      width: 250,
                      height: 250,
                      objectFit: "fill",
                      border: "3px solid #deef21",
                    }}
                  />
                </div>
              </div>

              {/* RIGHT */}
              <div className="col-12 col-md-7 text-center">
                <p className="fw-bold mb-3 fs-5" style={{ color: "#ff4f00" }}>
                  રામ નામ / राम नाम / RAM Name
                </p>

                <div
                  className="rounded-4 mb-3 p-3"
                  style={{
                    background: "#fff",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-dark fs-6">
                      {getText(
                        "Total RAM Name:",
                        "कुल राम नाम:",
                        "કુલ રામ નામ:",
                      )}
                    </small>
                    <p
                      className="fw-bold mb-0 fs-5"
                      style={{ color: "#ff4f00" }}
                    >
                      {prevRamNam}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-dark fs-6">
                      {getText("Count:", "गणना:", "ગણતરી:")}
                    </small>
                    <p className="fw-bold mb-0 text-success fs-6">
                      {todayCount.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="d-flex justify-content-center mb-3">
                  <MalaSection {...sharedMalaProps} size={200} />
                </div>

                <Button
                  disabled={loading || todayCount === 0}
                  onClick={handleSubmit}
                  className="w-100 rounded-4 py-2 mb-4"
                  style={{
                    fontSize: "1.1rem",
                    boxShadow: "0 8px 18px rgba(18,124,42,0.35)",
                    backgroundColor: "#076d07",
                    color: "#fff",
                    border: "none",
                  }}
                >
                  {loading
                    ? "Submitting..."
                    : getText(
                        "Submit Your Jaap",
                        "जाप सबमिट करें",
                        "જાપ સબમિટ કરો",
                      )}
                </Button>

                <button
                  className="btn btn-sm btn-outline-danger rounded-3 shadow-sm fw-bold"
                  onClick={() => navigate("/digital-history")}
                >
                  <i className="bi bi-eye-fill me-1" />
                  View History
                </button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* ═══════════ MOBILE ═══════════ */}
      <div className="d-md-none">
        <Card className="border-0 rounded-4 shadow w-100">
          <Card.Body className="p-3 pt-0 mt-0 text-center">
            <div className="d-flex justify-content-center mb-1 mt-0 pt-0">
              <img
                src="/assets/logo/RAMBhagvan.png"
                alt="Shri Ram"
                className="rounded-circle shadow"
                style={{
                  width: 100,
                  height: 100,
                  objectFit: "fill",
                  border: "3px solid #deef21",
                  marginTop: 0,
                }}
              />
            </div>

            <p className="fw-bold fs-6 mb-3" style={{ color: "#ff4f00" }}>
              રામ નામ / राम नाम / RAM Name
            </p>

            <div
              className="bg-white rounded-3 p-3 mb-3 shadow-sm"
              style={{ fontSize: "0.95rem" }}
            >
              <div className="d-flex justify-content-between mb-2">
                <small>
                  {getText("Total RAM Name:", "कुल राम नाम:", "કુલ રામ નામ:")}
                </small>
                <strong style={{ color: "#ff4f00" }}>{prevRamNam}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <small>{getText("Count:", "गणना:", "ગણતરી:")}</small>
                <strong className="text-success">
                  {todayCount.toLocaleString()}
                </strong>
              </div>
            </div>

            <div className="d-flex justify-content-center mb-3">
              <MalaSection {...sharedMalaProps} size={200} />
            </div>

            <Button
              disabled={loading || todayCount === 0}
              onClick={handleSubmit}
              className="w-100 rounded-3 mb-2"
              style={{
                backgroundColor: "#076d07",
                border: "none",
                color: "#fff",
              }}
            >
              {loading
                ? "Submitting..."
                : getText(
                    "Submit Your Jaap",
                    "जाप सबमिट करें",
                    "જાપ સબમિટ કરો",
                  )}
            </Button>

            <button
              className="btn btn-sm btn-outline-danger rounded-3 shadow-sm fw-bold mt-1"
              onClick={() => navigate("/digital-history")}
            >
              <i className="bi bi-eye-fill me-1" />
              View History
            </button>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default DigitalRamTapCard;
